/**
 * Keep one failing panel from taking the whole builder down.
 *
 * React unmounts the entire tree when a render throws. Wrapping each side
 * panel section means a bug in, say, the locomotion panel leaves the scene,
 * the module library and the other panels usable, shows what went wrong in
 * place, and reports it to the access log.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { Alert, Button, Text } from "@mantine/core";
import { describeThrown, reportError } from "../errorReporting";

interface SectionErrorBoundaryProps {
  /** Shown in the fallback and sent with the report. */
  name: string;
  children: ReactNode;
}

interface SectionErrorBoundaryState {
  message: string | null;
}

export class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, SectionErrorBoundaryState> {
  state: SectionErrorBoundaryState = { message: null };

  static getDerivedStateFromError(error: unknown): SectionErrorBoundaryState {
    return { message: describeThrown(error).message };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    const described = describeThrown(error);
    reportError({
      message: described.message,
      stack: `${described.stack ?? ""}\n${info.componentStack ?? ""}`.trim(),
      source: `panel:${this.props.name}`,
    });
  }

  render(): ReactNode {
    if (this.state.message === null) {
      return this.props.children;
    }
    return (
      <Alert color="red" variant="light" title={`${this.props.name} panel failed`} data-testid="section-error">
        <Text size="xs" style={{ wordBreak: "break-word" }}>
          {this.state.message}
        </Text>
        <Text size="xs" c="dimmed" mt={4}>
          The rest of the builder keeps working; this has been reported.
        </Text>
        <Button size="xs" variant="subtle" mt={6} onClick={() => this.setState({ message: null })}>
          Try again
        </Button>
      </Alert>
    );
  }
}
